import { useEffect, useMemo, useReducer } from 'react';
import {
  NavigationPageItemDto,
  VegaChartDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import { FullscreenWidgetModal, WidgetProps } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';

import { fetchTableFilterCustomization } from '../../api/portalGateway/tableFilterCustomization';
import { useFullscreenInstanceIds } from '../../hooks/useFullscreenInstanceIds';
import { InfoIconModal } from '../infoicon/InfoIconModal';
import { TableCustomFilter } from '../tablecustomization/TableCustomFilter';

import {
  getInitialControlState,
  vegaControlReducer,
} from './inputBinding/vegaControlState';
import { useVegaSchemeColors } from './chartColors';
import { useWithPagination } from './paginationHooks';
import { prepareSpecs } from './prepareSpecs';
import { VegaChartContent } from './VegaChartContent';

export type VegaChartProps = Pick<
  WidgetDto,
  'allowOpenInFullscreen' | 'id' | 'title' | 'collapsible' | 'infoIcon'
> &
  Pick<NavigationPageItemDto, 'pageId'> & {
    entity: VegaChartDto;
    instanceId: string;
  };

export function VegaChart({
  allowOpenInFullscreen,
  collapsible = false,
  entity,
  id: widgetId,
  infoIcon,
  title,
  instanceId,
  pageId,
}: VegaChartProps) {
  const { textColor, primaryColor, secondaryColor, masterColors } =
    useVegaSchemeColors();

  const { nonFullscreenSpec, fullscreenSpec, bindings } = useMemo(() => {
    return prepareSpecs(JSON.parse(entity.config.vegaSpecification), {
      masterColors,
      textColor,
      primaryColor,
      secondaryColor,
    });
  }, [
    entity.config.vegaSpecification,
    masterColors,
    textColor,
    primaryColor,
    secondaryColor,
  ]);

  const [controlState, controlDispatch] = useReducer(
    vegaControlReducer,
    bindings,
    getInitialControlState
  );

  const [fullscreenControlState, fullscreenControlDispatch] = useReducer(
    vegaControlReducer,
    bindings,
    getInitialControlState
  );

  useEffect(() => {
    controlDispatch({
      type: 'reset',
      payload: getInitialControlState(bindings),
    });
    // this should not be called on theme change
    // we should consider splitting the prepareSpecs function to remove theme dependency from bindings reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.config.vegaSpecification]);

  const labelData = useMemo(() => {
    const labelData = Object.entries(entity.config.labels ?? {}).map(
      ([key, label]) => ({
        key,
        label,
      })
    );

    // spec label data is the same between fullscreen and non fullscreen specs, so we can use either here to extract it
    const specData = nonFullscreenSpec.data ?? [];
    const specLabelData = specData.find((d) => d.name === 'labelData');

    if (
      specLabelData != null &&
      'values' in specLabelData &&
      Array.isArray(specLabelData.values)
    ) {
      for (const labelValue of specLabelData.values) {
        const entry = labelData.find(({ key }) => key === labelValue.key);

        if (!entry) {
          labelData.push(labelValue);
        }
      }
    }

    return labelData;
  }, [entity.config.labels, nonFullscreenSpec.data]);

  const { data: storedCustomFilterIds } = useQuery({
    queryKey: ['tableFilterCustomization', { widgetId }],
    queryFn: fetchTableFilterCustomization,
    enabled: entity.config.customFilter.length > 0,
  });

  const activeCustomFilterIds = useMemo(
    () => storedCustomFilterIds?.data.columns.map((c) => c.columnId) ?? [],
    [storedCustomFilterIds]
  );

  const [
    customFilterOpened,
    { close: closeCustomFilter, open: openCustomFilter },
  ] = useDisclosure();

  const {
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    cloneFullscreenInstanceState,
  } = useFullscreenInstanceIds({
    pageFilterInstanceId: instanceId,
    widgetFilterInstanceId: widgetId,
  });

  const [fullscreenOpened, { close: closeFullscreen, open: openFullscreen }] =
    useDisclosure(false, {
      onOpen: () => {
        cloneFullscreenInstanceState();
        fullscreenControlDispatch({ type: 'reset', payload: controlState });
      },
    });

  const [infoModalOpened, { close: closeInfoModal, open: openInfoModal }] =
    useDisclosure();

  const withPagination = useWithPagination(nonFullscreenSpec);

  return (
    <>
      <VegaChartContent
        activeCustomFilterIds={activeCustomFilterIds}
        bindings={bindings}
        collapsible={collapsible}
        controlDispatch={controlDispatch}
        controlState={controlState}
        customFilterConfigs={entity.config.customFilter}
        enableScrollElementToTop
        entity={entity}
        infoIcon={infoIcon}
        isFullscreen={false}
        labelData={labelData}
        openCustomFilter={openCustomFilter}
        openFullscreen={allowOpenInFullscreen ? openFullscreen : undefined}
        openInfoModal={openInfoModal}
        pageFilterInstanceId={instanceId}
        pageId={pageId}
        spec={nonFullscreenSpec}
        title={title}
        widgetId={widgetId}
        withPagination={withPagination}
        widgetInstanceId={widgetId}
      />
      {allowOpenInFullscreen && (
        <FullscreenWidgetModal
          onClose={closeFullscreen}
          opened={fullscreenOpened}
        >
          <VegaChartContent
            activeCustomFilterIds={activeCustomFilterIds}
            bindings={bindings}
            closeFullscreen={closeFullscreen}
            collapsible={collapsible}
            controlDispatch={fullscreenControlDispatch}
            controlState={fullscreenControlState}
            customFilterConfigs={entity.config.customFilter}
            dataTestId="vega-chart_widget_fullscreen"
            infoIcon={infoIcon}
            isFullscreen={true}
            entity={entity}
            labelData={labelData}
            openCustomFilter={openCustomFilter}
            openInfoModal={openInfoModal}
            pageFilterInstanceId={fullscreenInstanceStateId}
            pageId={pageId}
            showPageFilter={true}
            spec={fullscreenSpec}
            title={title}
            widgetId={widgetId}
            withPagination={false}
            widgetInstanceId={widgetFullscreenInstanceStateId}
          />
        </FullscreenWidgetModal>
      )}
      <InfoIconModal
        onClose={closeInfoModal}
        opened={infoModalOpened}
        headline={infoIcon?.headline}
        text={infoIcon?.text}
      />
      {entity.config.customFilter.length > 0 && (
        <TableCustomFilter
          activeCustomFilterIds={activeCustomFilterIds}
          customFilterColumns={entity.config.customFilter.map((filter) => ({
            id: filter.id,
            label: filter.filterConfig.label || filter.filterConfig.key,
            ...filter.filterConfig,
          }))}
          modalOpened={customFilterOpened}
          onClose={closeCustomFilter}
          widgetId={widgetId}
          widgetFilterInstanceIds={[widgetId, widgetFullscreenInstanceStateId]}
        />
      )}
    </>
  );
}
